import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { trackPage } from '../utils/analytics';

export default function Profile() {
  const { profile, user } = useAuth();

  useEffect(() => { trackPage('/profile'); }, []);

  if (!profile) {
    return (
      <div className="page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <p className="text-muted" style={{ fontFamily: 'var(--font-heading)', fontSize: '1.4rem' }}>
          Loading your profile...
        </p>
      </div>
    );
  }

  const hasScore   = profile.current_score !== null && profile.total_ratings_received >= 3;
  const isWeighted = profile.total_ratings_received >= 5;

  return (
    <div className="page" style={{ alignItems: 'center', paddingTop: 100 }}>
      <div className="container">

        <div className="text-center mb-24">
          <h2 style={{ fontFamily: 'var(--font-heading)', color: 'var(--gold)', fontSize: '2.2rem' }}>
            {profile.display_name}
          </h2>
          <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
            {user?.email}
          </p>
        </div>

        {/* Photo card */}
        <div className="card mb-24">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', color: 'var(--text)' }}>Your photo</h3>
            {profile.profile_photo_url && (
              <span className={`badge ${profile.photo_approved ? 'badge-live' : 'badge-pending'}`}>
                {profile.photo_approved ? 'Live' : 'Under review'}
              </span>
            )}
          </div>

          {profile.profile_photo_url ? (
            <img
              src={profile.profile_photo_url}
              alt="Your profile"
              onError={e => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
              style={{
                width: '100%',
                maxHeight: 320,
                objectFit: 'cover',
                borderRadius: 'var(--radius)',
                display: 'block',
              }}
            />
          ) : null}
          <div style={{
            display: profile.profile_photo_url ? 'none' : 'flex',
            border: '2px dashed var(--border)',
            borderRadius: 'var(--radius)',
            padding: '40px 20px',
            textAlign: 'center',
            color: 'var(--text-muted)',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}>
            <p style={{ fontSize: '0.9rem' }}>Photo unavailable or still processing.</p>
            <Link to="/onboarding">
              <button className="btn-primary" style={{ width: 'auto', padding: '10px 24px' }}>
                Re-upload
              </button>
            </Link>
          </div>

          {!profile.photo_approved && profile.profile_photo_url && (
            <p className="text-muted mt-16" style={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
              Your photo is in the moderation queue. Once approved, the internet can begin forming opinions.
            </p>
          )}
        </div>

        {/* Score card */}
        <div className="card mb-24">
          <h3 style={{ fontFamily: 'var(--font-heading)', color: 'var(--text)', marginBottom: 20 }}>
            Your score
          </h3>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
            <div>
              {hasScore ? (
                <>
                  <div className="score-display">{profile.current_score.toFixed(1)}</div>
                  <div className="score-label">Out of 10</div>
                </>
              ) : (
                <div style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '1rem',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  letterSpacing: '0.02em',
                }}>
                  {profile.total_ratings_received < 3 ? 'Still loading...' : 'Calculating...'}
                </div>
              )}
            </div>

            <div style={{ flex: 1 }}>
              <div style={{
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 'var(--radius)',
                padding: '12px 16px',
                fontSize: '0.8rem',
                lineHeight: 1.7,
                color: 'var(--text-muted)',
              }}>
                <div>
                  <span style={{ color: 'var(--text)' }}>{profile.total_ratings_received}</span>
                  {' '}people formed an opinion
                </div>
                <div style={{ marginTop: 4 }}>
                  Scoring mode:{' '}
                  <span style={{ color: isWeighted ? 'var(--gold)' : 'var(--text-muted)' }}>
                    {isWeighted ? 'Weighted' : 'Baseline'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {!hasScore && profile.total_ratings_received < 3 && (
            <p className="text-muted mt-16" style={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
              Score hidden until 3 ratings are received.{' '}
              {profile.total_ratings_received > 0
                ? `${3 - profile.total_ratings_received} more to go.`
                : 'Rate others to get noticed.'}
            </p>
          )}

          {hasScore && !isWeighted && (
            <p className="text-muted mt-16" style={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
              Baseline scoring is active. After 5 ratings, your score will be weighted
              by the quality of who is judging you.
            </p>
          )}

          {isWeighted && (
            <p className="text-muted mt-16" style={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
              Weighted scoring is active. Higher-rated users have more influence on your score.
            </p>
          )}
        </div>

        {hasScore && (
          <div className="card mb-24" style={{ padding: '20px 24px' }}>
            <ScoreBar score={profile.current_score} />
          </div>
        )}

        <Link to="/feed">
          <button className="btn-primary">Keep rating</button>
        </Link>

        <Link to="/settings">
          <button className="btn-ghost mt-16" style={{ width: '100%', textAlign: 'center' }}>
            Settings — change name or photo
          </button>
        </Link>

        <p className="text-center text-muted mt-16" style={{ fontSize: '0.75rem' }}>
          You cannot see who gave you specific verdicts. That is by design.
        </p>

      </div>
    </div>
  );
}

function ScoreBar({ score }) {
  const pct = ((score - 1) / 9) * 100;
  const label =
    score >= 8.5 ? 'Extremely valid' :
    score >= 7.0 ? 'Valid' :
    score >= 5.5 ? 'Mostly valid' :
    score >= 4.0 ? 'Mid' :
    score >= 2.5 ? 'Quite mid' :
    'Deeply, profoundly mid';

  const color =
    score >= 7.0 ? 'var(--valid-color)' :
    score >= 4.5 ? 'var(--gold)' :
    'var(--mid-color)';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Verdict
        </span>
        <span style={{ fontFamily: 'var(--font-heading)', fontSize: '1.2rem', color }}>
          {label}
        </span>
      </div>
      <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          background: color,
          borderRadius: 4,
          transition: 'width 0.6s ease',
          boxShadow: `0 0 10px ${color}`,
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Deeply mid</span>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Extremely valid</span>
      </div>
    </div>
  );
}
